// Where an effect plays. Event bindings fill one preallocated placement per kind and hand it to the effect
// primitives, which copy what they keep; nothing holds a placement past the play call.

import { Vector3 } from 'three'
import { NEUTRAL_OWNER, NO_SIM_ID, type OwnerValue, type SimId } from '@taxwar/net'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../render/geo/globeSpace'
import type { RouteDirection, RouteGeometry } from './routeGeometry'

/** A point on (or above) the globe: rings, bursts, rain and labels. */
export interface PointPlacement {
  readonly kind: 'point'
  /** Whose colour 'owner'-coloured effects take. */
  owner: OwnerValue
  /** Unit normal of the anchor. */
  readonly at: Vector3
  /** Radius (render units) the anchor sits at: the network altitude, or a missile's altitude for interceptions. */
  altitude: number
  /** Angular size of the thing the effect is about (radians): effect radii are multiples of it. */
  extent: number
  /** Money amount the event carries (floaters print it); 0 for events without one. */
  amount: number
  /** Duration of a sustained effect (sanctions rain) in seconds; 0 for events without one. */
  seconds: number
}

/** Two points: beams and launch streaks. */
export interface PathPlacement {
  readonly kind: 'path'
  owner: OwnerValue
  readonly from: Vector3
  fromAltitude: number
  readonly to: Vector3
  toAltitude: number
}

/** An immutable ready route reference: one shared static geometry plus the orientation to read it in. Built once per edge. */
export interface ReadyRoute {
  readonly kind: 'route'
  readonly geometry: RouteGeometry
  readonly direction: RouteDirection
}

/** The unset selection a route placement starts with; a routeTrail play must set one first (RouteTrailSystem). */
export type RouteSelection = { readonly kind: 'unset' } | ReadyRoute

/** The shared immutable unset selection every idle route slot resets to (never copied, never mutated). */
export const UNSET_ROUTE_SELECTION: RouteSelection = Object.freeze({ kind: 'unset' } as const)

/**
 * The preallocated route placement bindings fill: an owner and a discriminated selection, unset until a producer
 * assigns an existing ready route reference (created once per edge, never per play). No fake curve and no nullable
 * multi-flag state; a routeTrail play with an unset selection is a programmer error and throws, never draws nothing.
 */
export class RoutePlacement {
  readonly kind = 'route' as const
  owner: OwnerValue = NEUTRAL_OWNER
  selection: RouteSelection = UNSET_ROUTE_SELECTION

  /** Points the placement at an existing ready route reference (no allocation); the caller keeps owning it. */
  setRoute(owner: OwnerValue, route: ReadyRoute): void {
    this.owner = owner
    this.selection = route
  }
}

/** A missile strike in flight: from the owner's capital to its target, followed by its entity position. */
export interface FlightPlacement {
  readonly kind: 'flight'
  owner: OwnerValue
  strike: SimId
  readonly origin: Vector3
  readonly target: Vector3
  /** Salvos the strike resolves (impact or interception) before its flight ends. */
  salvos: number
}

export interface PlacementByKind {
  readonly point: PointPlacement
  readonly path: PathPlacement
  readonly route: RoutePlacement
  readonly flight: FlightPlacement
}
export type PlacementKind = keyof PlacementByKind

export function createPointPlacement(): PointPlacement {
  return { kind: 'point', owner: NEUTRAL_OWNER, at: new Vector3(), altitude: GLOBE_ALTITUDE.network, extent: 0, amount: 0, seconds: 0 }
}

export function createPlacements(): PlacementByKind {
  return {
    point: createPointPlacement(),
    path: { kind: 'path', owner: NEUTRAL_OWNER, from: new Vector3(), fromAltitude: GLOBE_ALTITUDE.network, to: new Vector3(), toAltitude: GLOBE_ALTITUDE.network },
    route: new RoutePlacement(),
    flight: { kind: 'flight', owner: NEUTRAL_OWNER, strike: NO_SIM_ID, origin: new Vector3(), target: new Vector3(), salvos: 0 },
  }
}

/** Fills every field of a point placement on the network surface. */
export function placePoint(out: PointPlacement, owner: OwnerValue, latDeg: number, lonDeg: number, extent: number, amount: number, seconds: number): PointPlacement {
  out.owner = owner
  latLonToVector3(latDeg, lonDeg, 1, out.at)
  out.altitude = GLOBE_ALTITUDE.network
  out.extent = extent
  out.amount = amount
  out.seconds = seconds
  return out
}

/** Fills every field of a path placement between two network-surface points. */
export function placePath(out: PathPlacement, owner: OwnerValue, fromLat: number, fromLon: number, toLat: number, toLon: number): PathPlacement {
  out.owner = owner
  latLonToVector3(fromLat, fromLon, 1, out.from)
  out.fromAltitude = GLOBE_ALTITUDE.network
  latLonToVector3(toLat, toLon, 1, out.to)
  out.toAltitude = GLOBE_ALTITUDE.network
  return out
}
